// About.js - Final Updated Version
import { useState, useEffect } from 'react';
import axios from 'axios';

function About() {
  const [features, setFeatures] = useState([]);
  const [guides, setGuides] = useState([]);
  const [aboutPage, setAboutPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingGuides, setLoadingGuides] = useState(true);
  const [loadingAboutPage, setLoadingAboutPage] = useState(true);

  useEffect(() => {
    loadAboutPageData();
    loadFeatures();
    loadGuides();
  }, []);

  const loadAboutPageData = async () => {
    try {
      const response = await axios.get('https://travel-agency-server-lilac.vercel.app/api/about');
      if (response.data.success) {
        setAboutPage(response.data.aboutPage);
      } else {
        // Default data if API fails
        setAboutPage({
          aboutImage: 'assets/img/about.jpg',
          aboutText: 'At Asian Travel Agency, we are dedicated to creating comfortable, reliable, and memorable travel experiences for every traveler...',
          missionStatement: 'Our experienced team focuses on quality service, smooth planning, and customer satisfaction...',
          visionStatement: 'With premium transportation, carefully selected accommodations, and round-the-clock support...',
          stats: {
            toursCompleted: 1000,
            happyClients: 5000,
            destinations: 50,
            yearsExperience: 15
          }
        });
      }
    } catch (error) {
      console.error('Error loading about page data:', error);
      // Set default data
      setAboutPage({
        aboutImage: 'assets/img/about.jpg',
        aboutText: 'At Asian Travel Agency, we are dedicated to creating comfortable, reliable, and memorable travel experiences for every traveler. From peaceful natural escapes to culturally rich destinations, we carefully design each journey with attention to detail and personal care.',
        missionStatement: 'Our experienced team focuses on quality service, smooth planning, and customer satisfaction—so you can relax and enjoy every moment of your trip. Whether you are traveling for leisure, family vacations, or group tours, we ensure a seamless and enjoyable experience from start to finish.',
        visionStatement: 'With premium transportation, carefully selected accommodations, and round-the-clock support, we turn every road into beauty and every journey into a lasting memory.',
        stats: {
          toursCompleted: 1000,
          happyClients: 5000,
          destinations: 50,
          yearsExperience: 15
        }
      });
    } finally {
      setLoadingAboutPage(false);
    }
  };

  const loadGuides = async () => {
    try {
      const response = await axios.get('https://travel-agency-server-lilac.vercel.app/api/guides');
      if (response.data.success) {
        const activeGuides = (response.data.guides || [])
          .filter(guide => guide.isActive === true)
          .sort((a, b) => a.order - b.order)
          .slice(0, 4);
        setGuides(activeGuides);
      }
    } catch (error) {
      console.error('Error loading guides:', error);
      setGuides([]);
    } finally {
      setLoadingGuides(false);
    }
  };

  const loadFeatures = async () => {
    try {
      const response = await axios.get('https://travel-agency-server-lilac.vercel.app/api/about-features');
      if (response.data.success) {
        const activeFeatures = (response.data.features || [])
          .filter(feature => feature.isActive === true)
          .sort((a, b) => a.order - b.order);
        setFeatures(activeFeatures);
      } else {
        setFeatures([]);
      }
    } catch (error) {
      console.error('Error loading features:', error);
      setFeatures([]);
    } finally {
      setLoading(false);
    }
  };

  // Split features into two columns for display
  const firstColumn = features.slice(0, Math.ceil(features.length / 2));
  const secondColumn = features.slice(Math.ceil(features.length / 2));

  if (loading || loadingGuides || loadingAboutPage) {
    return (
      <div className="container-xxl py-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading about page...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Hero Header */}
      <div className="container-fluid bg-primary py-5 mb-5 hero-header">
        <div className="container py-5">
          <div className="row justify-content-center py-5">
            <div className="col-lg-10 pt-lg-5 mt-lg-5 text-center">
              <h1 className="display-3 text-white animated slideInDown">
                About Us
              </h1>
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb justify-content-center">
                  <li className="breadcrumb-item">
                    <a href="/">Home</a>
                  </li>
                  <li className="breadcrumb-item text-white active" aria-current="page">
                    About
                  </li>
                </ol>
              </nav>
            </div>
          </div>
        </div>
      </div>

      {/* About Start - Cloudinary Image সহ */}
      <div className="container-xxl py-5">
        <div className="container">
          <div className="row g-5">
            <div className="col-lg-6 wow fadeInUp" data-wow-delay="0.1s" style={{ minHeight: 400 }}>
              <div className="position-relative h-100">
                <img
                  className="img-fluid position-absolute w-100 h-100"
                  src={aboutPage?.aboutImage || 'assets/img/about.jpg'}
                  alt="Asian Travel Agency Team"
                  style={{ objectFit: "cover", borderRadius: '10px' }}
                  onError={(e) => {
                    e.target.src = 'assets/img/about.jpg';
                  }}
                />
              </div>
            </div>
            <div className="col-lg-6 wow fadeInUp" data-wow-delay="0.3s">
              <h6 className="section-title bg-white text-start text-primary pe-3">
                About Us
              </h6>
              <h1 className="mb-4">
                Welcome to <span className="text-primary">Asian Travel Agency</span>
              </h1>
              <p className="mb-3">
                {aboutPage?.aboutText}
              </p>
              <p className="mb-3">
                {aboutPage?.missionStatement}
              </p>
              <p className="mb-4">
                {aboutPage?.visionStatement}
              </p>

              {/* Dynamic Features Section */}
              <div className="row gy-2 gx-4 mb-4">
                {loading ? (
                  <div className="col-12">
                    <div className="text-center py-3">
                      <div className="spinner-border spinner-border-sm text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-2 text-muted small">Loading features...</p>
                    </div>
                  </div>
                ) : features.length === 0 ? (
                  <div className="col-12 text-center">
                    <p className="text-muted">No features available</p>
                  </div>
                ) : (
                  <>
                    <div className="col-sm-6">
                      {firstColumn.map((feature, index) => (
                        <p key={index} className="mb-2 d-flex align-items-center">
                          <i className={`${feature.icon || 'fa fa-arrow-right text-primary me-2'}`}></i>
                          <span className="ms-2">{feature.title}</span>
                        </p>
                      ))}
                    </div>
                    <div className="col-sm-6">
                      {secondColumn.map((feature, index) => (
                        <p key={index + firstColumn.length} className="mb-2 d-flex align-items-center">
                          <i className={`${feature.icon || 'fa fa-arrow-right text-primary me-2'}`}></i>
                          <span className="ms-2">{feature.title}</span>
                        </p>
                      ))}
                    </div>
                  </>
                )}
              </div>

              <a className="btn btn-primary py-3 px-5 mt-2" href="/contact">
                Read More
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Team Start */}
      <div className="container-xxl py-5">
        <div className="container">
          <div className="text-center wow fadeInUp" data-wow-delay="0.1s">
            <h6 className="section-title bg-white text-center text-primary px-3">
              Team Member
            </h6>
            <h1 className="mb-5">Meet Our Officials</h1>
          </div>

          {loadingGuides ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2 text-muted">Loading guides...</p>
            </div>
          ) : guides.length === 0 ? (
            <div className="text-center py-5">
              <p className="text-muted">No guides available at the moment</p>
            </div>
          ) : (
            <div className="row g-4 justify-content-center">
              {guides.map((guide, index) => (
                <div key={guide._id} className="col-lg-3 col-md-6 wow fadeInUp" data-wow-delay={`${0.1 + index * 0.2}s`}>
                  <div className="team-item">
                    <div className="overflow-hidden">
                      <img
                        className="img-fluid"
                        src={guide.image || 'assets/img/default-guide.jpg'}
                        alt={guide.name}
                        style={{ width: '100%', height: '250px', objectFit: 'cover' }}
                        onError={(e) => {
                          e.target.src = 'assets/img/default-guide.jpg';
                        }}
                      />
                    </div>
                    <div
                      className="position-relative d-flex justify-content-center"
                      style={{ marginTop: "-19px" }}
                    >
                      {guide.socialLinks?.facebook && (
                        <a className="btn btn-square mx-1" href={guide.socialLinks.facebook} target="_blank" rel="noopener noreferrer">
                          <i className="fab fa-facebook-f"></i>
                        </a>
                      )}
                      {guide.socialLinks?.twitter && (
                        <a className="btn btn-square mx-1" href={guide.socialLinks.twitter} target="_blank" rel="noopener noreferrer">
                          <i className="fab fa-twitter"></i>
                        </a>
                      )}
                      {guide.socialLinks?.instagram && (
                        <a className="btn btn-square mx-1" href={guide.socialLinks.instagram} target="_blank" rel="noopener noreferrer">
                          <i className="fab fa-instagram"></i>
                        </a>
                      )}
                    </div>
                    <div className="text-center p-4">
                      <h5 className="mb-1 fw-bold text-dark">{guide.name}</h5>

                      {/* Designation underline - Green */}
                      <div className="designation mb-3">
                        <p className="text-success mb-0 fw-medium" style={{
                          fontSize: '0.9rem',
                          position: 'relative',
                          display: 'inline-block',
                          paddingBottom: '5px'
                        }}>
                          {guide.designation}
                          <span style={{
                            position: 'absolute',
                            bottom: 0,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: '30px',
                            height: '2px',
                            backgroundColor: '#198754',
                            borderRadius: '2px'
                          }}></span>
                        </p>
                      </div>

                      {/* Bio in quote style - Green accents */}
                      {guide.bio && (
                        <div className="bio-quote mt-4">
                          <i className="fas fa-quote-left text-success me-1" style={{ fontSize: '0.8rem' }}></i>
                          <span className="text-muted small" style={{
                            fontStyle: 'normal',
                            fontSize: '0.85rem',
                            lineHeight: '1.6'
                          }}>
                            {guide.bio.length > 80 ? `${guide.bio.substring(0, 80)}...` : guide.bio}
                          </span>
                          <i className="fas fa-quote-right text-success ms-1" style={{ fontSize: '0.8rem' }}></i>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Stats Section */}
      <div className="container-xxl py-5 bg-light">
        <div className="container">
          <div className="row g-4">
            <div className="col-lg-3 col-md-6 wow fadeInUp" data-wow-delay="0.1s">
              <div className="d-flex">
                <div className="flex-shrink-0 btn-lg-square rounded-circle bg-primary">
                  <i className="fa fa-plane text-white"></i>
                </div>
                <div className="ps-3">
                  <h2 className="mb-0" data-toggle="counter-up">
                    {aboutPage?.stats?.toursCompleted?.toLocaleString() || '1000+'}
                  </h2>
                  <p className="text-primary mb-0">Tours Completed</p>
                </div>
              </div>
            </div>
            <div className="col-lg-3 col-md-6 wow fadeInUp" data-wow-delay="0.3s">
              <div className="d-flex">
                <div className="flex-shrink-0 btn-lg-square rounded-circle bg-primary">
                  <i className="fa fa-users text-white"></i>
                </div>
                <div className="ps-3">
                  <h2 className="mb-0" data-toggle="counter-up">
                    {aboutPage?.stats?.happyClients?.toLocaleString() || '5000+'}
                  </h2>
                  <p className="text-primary mb-0">Happy Clients</p>
                </div>
              </div>
            </div>
            <div className="col-lg-3 col-md-6 wow fadeInUp" data-wow-delay="0.5s">
              <div className="d-flex">
                <div className="flex-shrink-0 btn-lg-square rounded-circle bg-primary">
                  <i className="fa fa-map-marker-alt text-white"></i>
                </div>
                <div className="ps-3">
                  <h2 className="mb-0" data-toggle="counter-up">
                    {aboutPage?.stats?.destinations || '50+'}
                  </h2>
                  <p className="text-primary mb-0">Destinations</p>
                </div>
              </div>
            </div>
            <div className="col-lg-3 col-md-6 wow fadeInUp" data-wow-delay="0.7s">
              <div className="d-flex">
                <div className="flex-shrink-0 btn-lg-square rounded-circle bg-primary">
                  <i className="fa fa-award text-white"></i>
                </div>
                <div className="ps-3">
                  <h2 className="mb-0" data-toggle="counter-up">
                    {aboutPage?.stats?.yearsExperience || '15+'}
                  </h2>
                  <p className="text-primary mb-0">Years Experience</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="container-xxl py-5">
        <div className="container">
          <div className="bg-primary rounded p-4">
            <div className="row align-items-center">
              <div className="col-lg-8">
                <h2 className="text-white mb-0">Ready to Start Your Journey?</h2>
                <p className="text-white mb-0">
                  Contact us today to plan your perfect vacation.
                </p>
              </div>
              <div className="col-lg-4 text-lg-end">
                <a className="btn btn-light py-3 px-5" href="/contact">
                  Get Started Now
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default About;